import {Inject, Injectable} from '@angular/core';
import {ComponentStore} from '@ngrx/component-store';
import {switchMap, tap, withLatestFrom} from 'rxjs/operators';
import {EMPTY, Observable} from 'rxjs';
import {HttpErrorResponse} from "@angular/common/http";
import firebase from "firebase/compat/app";
import {onValue, ref} from "firebase/database";
import * as _ from 'lodash';
import Unsubscribe = firebase.Unsubscribe;
import {DepotModel} from "../models/depot.model";
import {DepotsHttpService} from "../services/depots-http.service";

export interface DepotsComponentState {
  depots: DepotModel[];
  cursor?: string;
  loading: boolean;
  error: string | null;
}

@Injectable()
export class DepotsComponentStore extends ComponentStore<DepotsComponentState> {

  // Selectors
  readonly depots$ = this.select((state) => state.depots);
  readonly loading$ = this.select((state) => state.loading);
  readonly error$ = this.select((state) => state.error);
  readonly cursor$ = this.select((state) => state.cursor);

  vm$ = this.select({
    depots: this.depots$,
    loading: this.loading$,
    error: this.error$,
    cursor: this.cursor$
  })

  private channelsUnsubscribeMap: Map<string, Unsubscribe> = new Map();

  constructor(
    @Inject(DepotsHttpService) private depotsHttpService: DepotsHttpService,
    @Inject('firebaseService') private firebaseService
  ) {
    super({
      depots: [],
      cursor: '',
      loading: false,
      error: null,
    });
  }

  // Updaters
  readonly onSetDepots = this.updater((state: DepotsComponentState, value: {
    depots: DepotModel[],
    cursor?: string
  }) => ({
    ...state,
    depots: _.uniq([...state.depots, ...value.depots]),
    cursor: value.cursor,
    loading: false,
    error: null,
  }));

  readonly updateAgent = this.updater((state: DepotsComponentState, updatedAgent: DepotModel) => ({
    ...state,
    depots: state.depots.map((a: DepotModel) => {
        return updatedAgent.id === a.id ? {...a, ...updatedAgent} : a;
      }
    ),
  }));

  // Updaters
  readonly onResetDepots = this.updater((state: DepotsComponentState) => ({
    ...state,
    depots: [],
    cursor: null,
    loading: false,
    error: null,
  }));

  readonly setLoading = this.updater((state, loading: boolean) => ({
    ...state,
    loading,
  }));

  readonly setError = this.updater((state, error: HttpErrorResponse) => ({
    ...state,
    error: error.message,
    loading: false,
  }));

  // Effect for fetching depots
  readonly getDepots = this.effect(
    (trigger$: Observable<any>) =>
      trigger$.pipe(
        tap(() => this.setLoading(true)), // Set loading state
        withLatestFrom(this.cursor$, this.depots$), // Combine with cursor from the state
        switchMap(([params, cursor, depots]) => {
            //TODO clean subscribed depots
            if (params.ids?.length) {

              const ids = depots.map(a => a.id);
              // Find missing IDs (those NOT in the store)
              const idsToRequest = params.ids.filter(id => !ids.includes(id));

              if (idsToRequest.length === 0) {
                // All requested IDs exist in the store, SKIP API call
                this.setLoading(false);
                return EMPTY;
              }

              // Update params to fetch only missing IDs
              params = {...params, ids: idsToRequest};
            }
            return this.depotsHttpService.getAll({...params, cursor: cursor || ''}).pipe(
              tap({
                next: (response) => {

                  //Subscribe to depots
                  response.list?.forEach((depot: DepotModel) => {
                    this.subscribe(depot.id);
                  })
                  return this.onSetDepots({depots: response.list, cursor: response.cursor});
                },
                error: (err) => this.setError(err.message || 'Failed to fetch depots'),
                finalize: () => this.setLoading(false)
              })
            )
          }
        )
      )
  );

  readonly removeDepots = this.updater(
    (state, ids: string[]) => ({
      ...state,
      depots: state.depots.filter((a: DepotModel) => !ids.includes(a.id))
    })
  );


  // Reset
  readonly resetDepots = this.effect<void>((trigger$) =>
    trigger$.pipe(
      tap(() => {
        this.unSubscribeAll();
        this.onResetDepots();
      })
    )
  );

  private unSubscribeAll(): void {
    for (const [id, channelUnsubscribe] of this.channelsUnsubscribeMap) {
      channelUnsubscribe();
    }
    this.channelsUnsubscribeMap.clear();
  }

  // Convert Firebase `onValue` into an observable
  public subscribe(id: string) {
    const channel = this.firebaseService.channel;
    const database = this.firebaseService.database;

    const path = `${channel.channelId}/${'depots_changed'}/${id}`;
    const dbRef = ref(database, path);

    // Listen for real-time updates
    const unsubscribe = onValue(
      dbRef,
      (snapshot) => {
        const depot: DepotModel = snapshot.val();
        if (!!depot) {
          this.updateAgent(depot);
        }
      }
    );

    // Cleanup when subscription is closed
    this.channelsUnsubscribeMap.set(id, unsubscribe);
  }

}
